import React, {
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';
import Button from '@mui/material/Button';
import Webcam from 'react-webcam';
import { createPortal } from 'react-dom';
import GalleryIcon from 'assets/svg/GalleryIcon';
import styles from 'components/common/common.module.scss';
import { getBytesToMegabytes } from 'utils/helper';
import {
  MAX_FILE_SIZE_IN_MB,
  MultimediaType,
} from 'utils/constants';
import { mimeTypesByCategory } from 'utils/files';

const videoConstraints = {
  width: 1280,
  height: 720,
  // facingMode: "user",
  facingMode: 'environment',
};

const displayableExtension = new Set(mimeTypesByCategory[MultimediaType.ProfilePicture]);

type Props = {
  getImage?: (file: string) => void;
  text?: string;
  imageBase64?: string;
  acceptExtensions?: string;
  disabled?: boolean;
};

type UploadActionSheetProps = {
  onChooseFromGallery: () => void;
  onOpenCamera: () => void;
  onClose: () => void;
};

function UploadActionSheet({
  onChooseFromGallery,
  onOpenCamera,
  onClose,
}: UploadActionSheetProps) {
  return (
    <div
      className="uploadModalWrapper"
      role="dialog"
      aria-modal="true"
      aria-label="Choose an upload option"
    >
      <div className="uploaderContainer">
        <div className="uploaderContent">
          <Button
            className="uploaderModalButton uploaderModalButtonDivider"
            onClick={onChooseFromGallery}
          >
            From Gallery
          </Button>
          <Button className="uploaderModalButton" onClick={onOpenCamera}>
            Take Picture
          </Button>
        </div>
        <div className="uploaderContent">
          <Button className="uploaderModalButton" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function UploaderElement({
  getImage,
  text = 'Upload your picture here',
  imageBase64,
  acceptExtensions,
  disabled,
}: Props) {
  const [image, setImage] = useState<string>(imageBase64 as string);
  const [file, setFile] = useState<File | null>(null);

  const [isOpenModal, setIsOpenModal] = useState(false);

  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const cameraContainerRef = useRef<HTMLDivElement | null>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraErrorMessage, setCameraErrorMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const getImageRef = useRef(getImage);

  useEffect(() => {
    getImageRef.current = getImage;
  }, [getImage]);

  const chooseFromGallery = () => {
    if (inputFileRef && inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const fileExtension = useMemo(() => {
    if (image) {
      return image.split(';')[0].split('/')[1];
    }
    return null;
  }, [image]);

  const uploaderModalHandler = useCallback(() => {
    setIsOpenModal((prevState) => !prevState);
  }, []);

  const closeUploaderModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  const onSelectFile = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const file = target?.files?.[0];
    const fileSize = getBytesToMegabytes(file?.size);
    if (fileSize > MAX_FILE_SIZE_IN_MB) {
      setErrorMessage(`Unsupported file size. Please try to upload a file of less or equal to ${MAX_FILE_SIZE_IN_MB} Megabytes`);
      uploaderModalHandler();
      return;
    }
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (readerEvt) => {
      setFile(file);
      setImage(readerEvt.target?.result as string);
      setErrorMessage('');
      uploaderModalHandler();
    };
    reader.onerror = (error) => {
      console.error('Error: ', error);
    };
  };

  const webcamRef = useRef(null);
  const openCamera = useCallback(() => {
    if (!isCameraOpen) {
      closeUploaderModal();
      setCameraErrorMessage('');
      setIsCameraOpen(true);
    }
  }, [closeUploaderModal, isCameraOpen]);

  const handleCameraError = useCallback((error: string | DOMException) => {
    const errorName = typeof error === 'string' ? error : error.name;
    const permissionErrors = new Set([
      'NotAllowedError',
      'PermissionDeniedError',
      'SecurityError',
    ]);

    setCameraErrorMessage(
      permissionErrors.has(errorName)
        ? 'Camera permission is blocked. Enable camera access in your browser or device settings, then try again.'
        : 'Unable to open the camera. Check that a camera is available and not being used by another application.',
    );
  }, []);

  const capturePhoto = useCallback(() => {
    if (webcamRef && webcamRef.current) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const imageSrc = webcamRef.current.getScreenshot();
      setIsCameraOpen(false);
      setImage(imageSrc);
    }
  }, [webcamRef]);

  const closeCamera = useCallback(() => {
    setIsCameraOpen(false);
  }, []);

  useEffect(() => {
    let animationFrame: number | undefined;

    if (isCameraOpen) {
      // The upload modal restores the previous scroll position before the inline camera mounts.
      // Wait for that render to finish, then bring the newly mounted camera into view.
      animationFrame = window.requestAnimationFrame(() => {
        cameraContainerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    }

    return () => {
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [isCameraOpen]);

  useEffect(() => {
    getImageRef.current?.(image);
  }, [image]);

  useEffect(() => {
    setImage(imageBase64 as string);
  }, [imageBase64]);

  useEffect(() => {
    if (!isOpenModal) {
      return undefined;
    }

    const scrollPosition = window.scrollY;
    const previousBodyTop = document.body.style.top;
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;

    document.body.style.top = `-${scrollPosition}px`;
    document.body.classList.add('lock-scroll');

    return () => {
      document.body.classList.remove('lock-scroll');
      document.body.style.top = previousBodyTop;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, scrollPosition);
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    };
  }, [isOpenModal]);

  return (
    <>
      {errorMessage && (
        <p className="error-message mb-2">{errorMessage.replaceAll(',', ', ')}</p>
      )}
      {isCameraOpen && (
        <div ref={cameraContainerRef} className="cameraContainer">
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            onUserMedia={() => setCameraErrorMessage('')}
            onUserMediaError={handleCameraError}
            className={cameraErrorMessage ? 'cameraVideoHidden' : undefined}
            style={{ maxWidth: 550, width: '-webkit-fill-available' }}
          />
          {cameraErrorMessage && (
            <p className="cameraErrorMessage" role="alert">
              {cameraErrorMessage}
            </p>
          )}
          <div className="cameraActions">
            {!cameraErrorMessage && (
              <Button className={styles.capturePhoto} onClick={capturePhoto}>
                Capture Photo
              </Button>
            )}
            <Button className="closeCamera" onClick={closeCamera}>
              Close Camera
            </Button>
          </div>
        </div>
      )}
      <div className="text-center">
        {image && (
          <>
            {fileExtension === 'pdf' && (
              <object
                aria-label="pdf"
                data={image}
                type="application/pdf"
                width="100%"
                height="100%"
                className={styles.image}
              />
            )}
            {displayableExtension.has(fileExtension ?? '')
              && (
                <img
                  src={image}
                  alt={text ?? ''}
                  className={styles.image}
                />
              )}
              {fileExtension !== 'pdf' && !displayableExtension.has(fileExtension ?? '') && (
                <div className={styles.uploaderWrapper}>
                  <p>{file?.name}</p>
                </div>
              )}
            <p
              className={styles.capturePhoto}
              onKeyUp={() => {}}
              onClick={() => {
                setImage('');
                if (inputFileRef.current && inputFileRef.current.value) { inputFileRef.current.value = ''; }
              }}
            >
              Clear File
            </p>
          </>
        )}
        {!image && !isCameraOpen && (
          <div className={styles.uploaderWrapper}>
            <GalleryIcon />
            <p className={styles.uploadText}>{text}</p>
            <Button
              onClick={uploaderModalHandler}
              className={styles.uploadButton}
              disabled={disabled}
            >
              Upload
            </Button>
          </div>
        )}
        <input
          type="file"
          hidden
          ref={inputFileRef}
          accept={acceptExtensions || '*'}
          onChange={onSelectFile}
        />
      </div>
      {isOpenModal && createPortal(
        <UploadActionSheet
          onChooseFromGallery={chooseFromGallery}
          onOpenCamera={openCamera}
          onClose={closeUploaderModal}
        />,
        document.body,
      )}
    </>
  );
}

export default UploaderElement;
